import { useSubpantalla } from '../ui/navegacionReact';
import { useCallback, useEffect, useState } from 'react';
import { HardHat, UserCheck, History } from 'lucide-react';
import { api, ErrorApi, traerTodo } from '../api';
import { useAuth } from '../lib/contextoAuth';
import { etiquetaRol } from '../lib/roles';
import { useListaUrl, consultaLista } from '../lib/urlEstado';
import ListaEstandar from '../ui/ListaEstandar';
import Paginador from '../ui/Paginador';
import EstadoVacio from '../ui/EstadoVacio';
import ChipEstado from '../ui/ChipEstado';
import ModalConfirmar from '../ui/ModalConfirmar';
import NotaExplicativa from '../ui/NotaExplicativa';
import { textoDeAyuda } from '../ayudas/notas';
import TiempoRelativo from '../ui/TiempoRelativo';
import { Buscador, SelectFiltro } from '../ui/Filtros';
import Modal from '../ui/Modal';
import { Seccion, CampoValidado, Autocompletar } from '../ui/Formulario';
import { useAvisos } from '../lib/contextoAvisos';
import { Cargando } from '../ui/EstadoDePantalla';

const ESTADO_FISICO = {
  NUEVO: { etiqueta: 'Nuevo', color: 'verde' },
  BUENO: { etiqueta: 'Bueno', color: 'verde' },
  USABLE: { etiqueta: 'Usable', color: 'ambar' },
  BAJA: { etiqueta: 'Dado de baja', color: 'gris' },
};

const ESTADO_DEVOLUCION = [
  ['BUENO', 'Bueno'],
  ['NUEVO', 'Como nuevo'],
  ['USABLE', 'Usable, con desgaste'],
  ['DANADO', 'Dañado'],
  ['PERDIDO', 'No se devolvió'],
];

// Activos de la empresa: lo que NO se vende. Hasta aquí este módulo
// existía solo en la API — se podía registrar una laptop con `curl` y no
// desde ninguna pantalla. Es el mismo patrón que ya apareció con las
// ubicaciones y con los lotes: backend completo, pantalla que no lo usa,
// y la función parece inexistente.
export default function Activos({ contexto, irA }) {
  const [pestana, setPestana] = useState('piezas');
  useSubpantalla('pestana', pestana !== 'piezas', () => setPestana('piezas'), { valor: pestana, abrir: setPestana, enUrl: true, reemplaza: true });

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Activos</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Lo que la empresa usa y no vende: equipo, herramienta, vehículos.
          Cada pieza se identifica por su número de serie.
        </p>
      </div>

      <div className="border-b border-gray-200 flex gap-1 flex-wrap">
        {[['piezas', 'Piezas'], ['resguardos', 'Quién tiene qué']].map(([id, et]) => (
          <button key={id} type="button" onClick={() => setPestana(id)} aria-current={pestana === id ? 'page' : undefined}
                  className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${
                    pestana === id
                      ? 'border-[var(--color-marca)] text-[var(--color-marca)]'
                      : 'border-transparent text-gray-500 hover:text-gray-800'
                  }`}>
            {et}
          </button>
        ))}
      </div>

      {pestana === 'piezas' ? <PanelPiezas contexto={contexto} irA={irA} /> : <PanelResguardos />}
    </div>
  );
}

function PanelPiezas({ contexto, irA }) {
  const { tieneNivel } = useAuth();
  const avisos = useAvisos();
  const { filtros, pagina, porPagina, desplazamiento, fijarFiltro, fijarPagina } =
    // `buscar` llega desde la búsqueda global al pulsar «ver todas».
    useListaUrl({ dados_de_baja: false, buscar: contexto?.buscar || '' },
                { porPagina: 100 });
  const deBaja = filtros.dados_de_baja;

  const [filas, setFilas] = useState([]);
  const [total, setTotal] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [accion, setAccion] = useState(null);   // {tipo, pieza}
  const [personas, setPersonas] = useState([]);

  const consulta = useCallback(
    (limite, desde) => `/activos?${consultaLista(filtros, {
      limite, desplazamiento: desde,
    })}`,
    [filtros],
  );

  const cargar = useCallback(() => {
    setCargando(true);
    return api.getConTotal(consulta(porPagina, desplazamiento))
      .then((r) => { setFilas(r.filas); setTotal(r.total); })
      .catch((e) => setError(e.message))
      .finally(() => setCargando(false));
  }, [consulta, porPagina, desplazamiento]);

  useEffect(() => {
    let vigente = true;
    // El texto se escribe letra a letra y ahora cada letra es una consulta
    // al servidor: sin este respiro, teclear "montacargas" son once.
    const t = setTimeout(() => {
      api.getConTotal(consulta(porPagina, desplazamiento))
        .then((r) => { if (vigente) { setFilas(r.filas); setTotal(r.total); } })
        .catch((e) => vigente && setError(e.message))
        .finally(() => vigente && setCargando(false));
    }, 250);
    return () => { vigente = false; clearTimeout(t); };
  }, [consulta, porPagina, desplazamiento]);

  const exportarTodo = useCallback(
    (alAvanzar) => traerTodo((limite, desde) => consulta(limite, desde), { alAvanzar }),
    [consulta],
  );

  useEffect(() => {
    // Para elegir a quién se le entrega. Va por el directorio y no por el
    // listado completo de usuarios: ese exige ADMIN y aquí basta con
    // ENCARGADO.
    api.get('/usuarios/directorio').then(setPersonas).catch(() => setPersonas([]));
  }, []);

  const columnas = [
    { etiqueta: 'Serie', valor: (p) => p.serie, clase: 'font-mono text-xs' },
    { etiqueta: 'Producto', valor: (p) => p.producto, clase: 'font-medium text-gray-900' },
    { etiqueta: 'SKU', valor: (p) => p.sku, clase: 'font-mono text-xs text-gray-500' },
    { etiqueta: 'Dónde', valor: (p) => p.ubicacion || '', clase: 'text-gray-500' },
    {
      etiqueta: 'Estado', valor: (p) => p.estado_fisico || '',
      render: (p) => <ChipEstado mapa={ESTADO_FISICO} clave={p.estado_fisico} />,
    },
    {
      etiqueta: 'Alta', tipo: 'fecha_hora', valor: (p) => p.fecha_ingreso,
      render: (p) => <TiempoRelativo valor={p.fecha_ingreso} />,
    },
    ...(deBaja
      ? [{ etiqueta: 'Motivo de baja', valor: (p) => p.motivo_baja || '',
           clase: 'text-gray-500' }]
      : []),
  ];

  return (
    <>
      <ListaEstandar
        titulo={deBaja ? 'Activos dados de baja' : 'Activos en uso'}
        subtitulo="Cada pieza es única y se sigue por su número de serie, desde que entra hasta que se da de baja."
        nota={
          <NotaExplicativa titulo="Activo no es lo mismo que mercancía">
            Un <strong>activo</strong> es lo que la empresa usa: una laptop, un
            montacargas, una pistola de etiquetas. La mercancía de venta vive en
            Productos.
          </NotaExplicativa>
        }
        filtros={
          <>
            <Buscador
              valor={filtros.buscar}
              onChange={(v) => fijarFiltro({ buscar: v })}
              placeholder="Serie, SKU o nombre…"
            />
            <SelectFiltro
              valor={deBaja ? 'baja' : 'uso'}
              onChange={(v) => fijarFiltro({ dados_de_baja: v === 'baja' })}
              opciones={[
                { valor: 'uso', etiqueta: 'En uso' },
                { valor: 'baja', etiqueta: 'Dados de baja' },
              ]}
            />
          </>
        }
        accionPrincipal={tieneNivel('ENCARGADO')
          ? { etiqueta: 'Registrar activo', onClick: () => setAccion({ tipo: 'alta' }) }
          : undefined}
        columnas={columnas}
        filas={filas}
        cargando={cargando}
        claveFila={(p) => p.serie}
        exportar={{ nombre: 'activos', columnas, obtenerTodo: exportarTodo }}
        paginador={
          <Paginador
            pagina={pagina}
            porPagina={porPagina}
            total={total}
            mostrados={filas.length}
            cargando={cargando}
            onPagina={fijarPagina}
          />
        }
        vacio={
          <EstadoVacio
            icono={HardHat}
            titulo={deBaja ? 'Nada dado de baja' : 'Todavía no hay activos'}
            descripcion={deBaja
              ? 'Aquí aparecerá el equipo que se retire de circulación.'
              : 'Registra el equipo de la empresa para poder saber dónde está y quién lo tiene.'}
          />
        }
        acciones={(p) => (tieneNivel('ENCARGADO') ? [
          { etiqueta: 'Entregar', onClick: () => setAccion({ tipo: 'entregar', pieza: p }) },
          { etiqueta: 'Devolver', onClick: () => setAccion({ tipo: 'devolver', pieza: p }) },
          { etiqueta: 'Historial', onClick: () => setAccion({ tipo: 'historial', pieza: p }) },
          ...(p.estado_fisico === 'BAJA'
            ? [{ etiqueta: 'Reactivar',
                 onClick: () => api.post(`/activos/${p.serie}/reactivar`, {})
                   .then(cargar).catch((e) => setError(e.message)) }]
            : [{ etiqueta: 'Dar de baja', peligro: true,
                 onClick: () => setAccion({ tipo: 'baja', pieza: p }) }]),
        ] : [])}
      />

      {error && (
        <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
          {error}
        </div>
      )}

      {accion?.tipo === 'alta' && (
        <ModalAlta onCerrar={() => setAccion(null)}
                   onListo={() => { setAccion(null); cargar(); }}
                   irA={irA} />
      )}

      {accion?.tipo === 'entregar' && (
        <ModalEntregar pieza={accion.pieza} personas={personas}
                       onCerrar={() => setAccion(null)}
                       onListo={(r) => {
                         avisos.exito(`${accion.pieza.serie} queda a nombre de `
                           + `${r.custodio}.`);
                         setAccion(null);
                         cargar();
                       }} />
      )}

      {accion?.tipo === 'devolver' && (
        <ModalDevolver pieza={accion.pieza}
                       onCerrar={() => setAccion(null)}
                       onListo={() => { setAccion(null); cargar(); }} />
      )}

      {accion?.tipo === 'historial' && (
        <ModalHistorial pieza={accion.pieza} onCerrar={() => setAccion(null)} />
      )}

      {accion?.tipo === 'baja' && (
        <ModalConfirmar
          peligro
          titulo={`Dar de baja ${accion.pieza.serie}`}
          descripcion="Sale de circulación. El historial de quién la tuvo se conserva, y la baja es reversible."
          cambios={[{ campo: 'Estado', antes: accion.pieza.estado_fisico, despues: 'BAJA' }]}
          pedirMotivo
          etiquetaConfirmar="Dar de baja"
          onConfirmar={async (motivo) => {
            await api.post(`/activos/${accion.pieza.serie}/baja`, { motivo });
            setAccion(null);
            cargar();
          }}
          onCerrar={() => setAccion(null)}
        />
      )}
    </>
  );
}

function ModalAlta({ onCerrar, onListo, irA }) {
  const [f, setF] = useState({
    sku: '', ubicacion: '', estado_fisico: 'NUEVO', cantidad: 1, series: '',
  });
  const [ubicaciones, setUbicaciones] = useState([]);
  // ANEXO-CLIENTE-01 H-1 (20 sep 2026). El buscador SOLO ofrece productos
  // que sí se pueden dar de alta aquí: los registrados como equipo de la
  // empresa. Antes era un campo libre, y quien tecleaba un SKU de venta
  // recibía «no es de tipo ACTIVO… usa un movimiento de ENTRADA», dos
  // palabras del sistema y ninguna salida.
  const [equipos, setEquipos] = useState(null);
  // Si aun así escribe uno de venta, el servidor lo rechaza (409) y aquí
  // se ofrece la salida con el SKU ya cargado.
  const [desvio, setDesvio] = useState(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  useEffect(() => {
    api.get('/ubicaciones').then((u) => {
      setUbicaciones(u);
      if (u.length) setF((s) => ({ ...s, ubicacion: s.ubicacion || u[0].codigo }));
    }).catch(() => {});
    api.get('/productos?tipo_inventario=ACTIVO&limite=500')
      .then((lista) => setEquipos(lista.map((p) => ({ id: p.sku, sku: p.sku, nombre: `${p.sku} — ${p.nombre}` }))))
      .catch(() => setEquipos([]));
  }, []);

  const elegido = f.sku ? (equipos || []).find((e) => e.sku === f.sku) || { id: null, nombre: f.sku } : null;

  return (
    <ModalConfirmar
      titulo="Registrar activo"
      descripcion="Da de alta una o varias piezas idénticas. Si no escribes las series, el sistema las genera."
      extra={
        <div className="space-y-5">
          <Seccion titulo="Qué se da de alta y dónde queda" columnas={1}>
            <label className="block">
              <span className="block text-sm font-semibold text-gray-700 mb-1">
                Qué equipo <span className="text-red-600" aria-hidden="true">*</span>
              </span>
              <Autocompletar
                opciones={equipos || []}
                valor={elegido}
                onChange={(o) => { setDesvio(null); setF((s) => ({ ...s, sku: (o?.sku || o?.nombre || '').trim().toUpperCase() })); }}
                placeholder={equipos && equipos.length === 0 ? 'Todavía no hay equipos registrados' : 'Busca por código o nombre…'}
              />
              <span className="block text-xs text-gray-500 mt-1">
                Aquí solo aparecen los productos registrados como equipo de la empresa.
                {equipos && equipos.length === 0 && (
                  <> No hay ninguno todavía: créalo en Productos eligiendo «Activo de la empresa».</>
                )}
              </span>
            </label>
            <CampoValidado etiqueta="Dónde queda" requerido valor={f.ubicacion}
                           validar={(v) => (v ? null : 'Elige dónde queda.')}>
              {(props) => (
                <select {...props} value={f.ubicacion} onChange={set('ubicacion')}>
                  {ubicaciones.map((u) => (
                    <option key={u.id} value={u.codigo}>{u.codigo} — {u.nombre}</option>
                  ))}
                </select>
              )}
            </CampoValidado>
          </Seccion>

          <Seccion titulo="Cuántas y en qué estado">
            <CampoValidado etiqueta="Estado físico" valor={f.estado_fisico}>
              {(props) => (
                <select {...props} value={f.estado_fisico} onChange={set('estado_fisico')}>
                  {['NUEVO', 'BUENO', 'USABLE'].map((v) => <option key={v}>{v}</option>)}
                </select>
              )}
            </CampoValidado>
            <CampoValidado etiqueta="Cuántas piezas" valor={f.cantidad}
                           ayuda="Se ignora si escribes las series abajo."
                           validar={(v) => (Number(v) >= 1 ? null : 'Al menos una.')}>
              {(props) => <input {...props} type="number" min="1" value={f.cantidad}
                                 onChange={set('cantidad')}
                                 className={`${props.className} tabular-nums`} />}
            </CampoValidado>
            <CampoValidado etiqueta="Series" ancho="completo" valor={f.series}
                           ayuda="Una por línea. Déjalo vacío y se generan solas."
                           validar={(v) => {
                             const l = v.split('\n').map((x) => x.trim()).filter(Boolean);
                             return new Set(l).size === l.length
                               ? null : 'Hay series repetidas: cada pieza lleva la suya.';
                           }}>
              {(props) => <textarea {...props} value={f.series} onChange={set('series')} rows={3}
                                    className={`${props.className} font-mono text-xs`} />}
            </CampoValidado>
          </Seccion>

          {/* Un error que no ofrece la salida está a medias: aquí va el
              botón que lleva a registrar la entrada con el SKU cargado. */}
          {desvio && irA && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <span><strong>{desvio}</strong> es mercancía de venta, no equipo.</span>
              <button type="button"
                      onClick={() => { onCerrar(); irA('escanear', { codigo: desvio }); }}
                      className="rounded-lg bg-[var(--color-marca)] px-3 py-2 text-sm font-semibold text-white">
                Abrir entrada de mercancía
              </button>
            </div>
          )}
        </div>
      }
      etiquetaConfirmar="Registrar"
      onConfirmar={async () => {
        if (!f.sku.trim()) throw new ErrorApi('Elige qué equipo se da de alta.');
        if (!f.ubicacion) throw new ErrorApi('Elige dónde queda.');
        const series = f.series.split('\n').map((x) => x.trim()).filter(Boolean);
        try {
          await api.post('/activos/registrar', {
            sku: f.sku.trim().toUpperCase(),
            ubicacion: f.ubicacion,
            estado_fisico: f.estado_fisico,
            cantidad: series.length || Number(f.cantidad),
            ...(series.length ? { series } : {}),
          });
        } catch (e) {
          // Solo el rechazo de «esto es mercancía, no equipo» lleva a la
          // entrada; un 409 por serie repetida o producto de baja, no.
          if (e instanceof ErrorApi && e.status === 409 && /mercancía de venta/.test(e.message || '')) {
            setDesvio(f.sku.trim().toUpperCase());
          }
          throw e;
        }
        onListo();
      }}
      onCerrar={onCerrar}
    />
  );
}

function ModalEntregar({ pieza, personas, onCerrar, onListo }) {
  const [f, setF] = useState({ id_custodio: '', lugar: '', motivo: '' });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  return (
    <ModalConfirmar
      titulo={`Entregar ${pieza.serie}`}
      descripcion={textoDeAyuda('activos.entrega')}
      extra={
        <Seccion titulo="La entrega" columnas={1}
                 descripcion="Dónde va a estar es lo que después permite ir a buscarla sin llamar a nadie.">
          <CampoValidado etiqueta="A quién se le entrega" requerido valor={f.id_custodio}
                         validar={(v) => (v ? null : 'Elige a quién se le entrega.')}>
            {(props) => (
              <select {...props} value={f.id_custodio} onChange={set('id_custodio')}>
                <option value="">Elige a la persona…</option>
                {personas.map((p) => (
                  <option key={p.id} value={p.id}>{p.nombre_completo} ({etiquetaRol(p.rol)})</option>
                ))}
              </select>
            )}
          </CampoValidado>
          <CampoValidado etiqueta="Dónde va a estar" valor={f.lugar}>
            {(props) => <input {...props} value={f.lugar} onChange={set('lugar')}
                               placeholder="Camioneta 4, obra de Monterrey, oficina…" />}
          </CampoValidado>
          <CampoValidado etiqueta="Para qué" valor={f.motivo}>
            {(props) => <input {...props} value={f.motivo} onChange={set('motivo')} />}
          </CampoValidado>
        </Seccion>
      }
      etiquetaConfirmar="Entregar"
      onConfirmar={async () => {
        if (!f.id_custodio) throw new ErrorApi('Elige a quién se le entrega.');
        const r = await api.post(`/activos/${pieza.serie}/resguardo`, {
          id_custodio: Number(f.id_custodio),
          lugar: f.lugar.trim() || null,
          motivo: f.motivo.trim() || null,
        });
        onListo(r);
      }}
      onCerrar={onCerrar}
    />
  );
}

function ModalDevolver({ pieza, onCerrar, onListo }) {
  const [estado, setEstado] = useState('BUENO');
  const [nota, setNota] = useState('');
  const grave = estado === 'DANADO' || estado === 'PERDIDO';

  return (
    <ModalConfirmar
      titulo={`Devolución de ${pieza.serie}`}
      peligro={grave}
      extra={
        <Seccion titulo="La devolución" columnas={1}>
          <CampoValidado etiqueta="¿En qué estado vuelve?" requerido valor={estado}>
            {(props) => (
              <select {...props} value={estado} onChange={(e) => setEstado(e.target.value)}>
                {ESTADO_DEVOLUCION.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
              </select>
            )}
          </CampoValidado>
          {/* Guía, no regla: el backend no exige la nota y marcarla como
              error sin poder bloquear el botón solo haría parecer que la
              pantalla está rota. Que sea obligatoria es una decisión de
              producto, no mía. */}
          <CampoValidado
            etiqueta="Nota" valor={nota}
            ayuda={grave
              ? 'Escríbela: la pieza se va a dar de baja, y esto es lo único que explicará por qué dentro de seis meses.'
              : undefined}
          >
            {(props) => <textarea {...props} value={nota} onChange={(e) => setNota(e.target.value)}
                                  rows={2} placeholder="Qué le pasó, si algo" />}
          </CampoValidado>
        </Seccion>
      }
      aviso={grave
        ? 'La pieza se dará de baja automáticamente: un activo que volvió roto o que no volvió no puede seguir figurando como disponible.'
        : null}
      etiquetaConfirmar="Registrar devolución"
      onConfirmar={async () => {
        await api.post(`/activos/${pieza.serie}/devolucion`,
                       { estado, nota: nota.trim() || null });
        onListo();
      }}
      onCerrar={onCerrar}
    />
  );
}

function ModalHistorial({ pieza, onCerrar }) {
  const [filas, setFilas] = useState(null);

  useEffect(() => {
    api.get(`/activos/${pieza.serie}/resguardos`)
      .then(setFilas).catch(() => setFilas([]));
  }, [pieza.serie]);

  return (
    <Modal
      titulo={`Por cuántas manos ha pasado ${pieza.serie}`}
      icono={History}
      ancho="max-w-lg"
      onCerrar={onCerrar}
    >
      <div className="space-y-3">

        {filas === null && <Cargando filas={4} />}
        {filas?.length === 0 && (
          <p className="text-sm text-gray-500">
            Nunca se ha entregado a nadie. Sigue en el almacén.
          </p>
        )}

        <div className="divide-y divide-gray-100">
          {(filas || []).map((r) => (
            <div key={r.id} className="py-2.5 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="font-medium text-gray-900">{r.custodio}</span>
                <ChipEstado
                  mapa={{ VIGENTE: { etiqueta: 'La tiene', color: 'azul' },
                          DEVUELTO: { etiqueta: 'Devuelta', color: 'gris' } }}
                  clave={r.estado}
                />
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {r.lugar ? `${r.lugar} · ` : ''}
                <TiempoRelativo valor={r.entregado_en} /> · {r.dias} día(s)
                {r.entregado_por ? ` · entregó ${r.entregado_por}` : ''}
              </div>
              {r.motivo && <div className="text-xs text-gray-600 mt-0.5">{r.motivo}</div>}
              {r.estado === 'DEVUELTO' && (
                <div className="text-xs text-gray-600 mt-0.5">
                  Volvió {r.estado_devolucion?.toLowerCase()}
                  {r.nota_devolucion ? ` — ${r.nota_devolucion}` : ''}
                  {r.recibido_por ? ` · recibió ${r.recibido_por}` : ''}
                </div>
              )}
            </div>
          ))}
        </div>

        <button type="button" onClick={onCerrar}
                className="w-full border border-gray-300 text-gray-700 font-semibold py-2 rounded-lg hover:bg-gray-50">
          Cerrar
        </button>
      </div>
    </Modal>
  );
}

// La pregunta que el módulo de custodio existe para contestar, como
// pantalla propia y no como detalle escondido en cada ficha.
function PanelResguardos() {
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [vigentes, setVigentes] = useState(true);

  useEffect(() => {
    let vivo = true;
    api.get(`/activos/resguardos?solo_vigentes=${vigentes}`)
      .then((d) => vivo && setFilas(d))
      .catch(() => vivo && setFilas([]))
      .finally(() => vivo && setCargando(false));
    return () => { vivo = false; };
  }, [vigentes]);

  const columnas = [
    { etiqueta: 'Quién', valor: (r) => r.custodio, clase: 'font-medium text-gray-900' },
    { etiqueta: 'Qué', valor: (r) => r.producto },
    { etiqueta: 'Serie', valor: (r) => r.serie, clase: 'font-mono text-xs' },
    { etiqueta: 'Dónde', valor: (r) => r.lugar || '', clase: 'text-gray-500' },
    {
      etiqueta: 'Desde', tipo: 'fecha_hora', valor: (r) => r.entregado_en,
      render: (r) => <TiempoRelativo valor={r.entregado_en} />,
    },
    {
      etiqueta: 'Días', alineado: 'derecha', valor: (r) => r.dias,
      render: (r) => (
        <span className={`tabular-nums ${r.dias > 180 && r.estado === 'VIGENTE'
          ? 'text-amber-700 font-semibold' : 'text-gray-600'}`}>
          {r.dias}
        </span>
      ),
    },
    { etiqueta: 'Entregó', valor: (r) => r.entregado_por || '', clase: 'text-gray-500' },
  ];

  return (
    <ListaEstandar
      titulo="Quién tiene qué"
      subtitulo="Un activo entregado sigue siendo de la empresa; lo que cambia es quién responde por él."
      filtros={
        <SelectFiltro
          valor={vigentes ? 'si' : 'no'}
          onChange={(v) => setVigentes(v === 'si')}
          opciones={[
            { valor: 'si', etiqueta: 'Entregados ahora' },
            { valor: 'no', etiqueta: 'Todo el historial' },
          ]}
        />
      }
      columnas={columnas}
      filas={filas}
      cargando={cargando}
      exportar={{ nombre: 'resguardos', columnas }}
      vacio={
        <EstadoVacio
          icono={UserCheck}
          titulo="Nadie tiene nada entregado"
          descripcion="Cuando entregues un activo a alguien, aparecerá aquí con su nombre y desde cuándo."
        />
      }
    />
  );
}

